/**
 * Wann die 3D-Welt ueberhaupt gebraucht wird
 * (Produktentscheidung vom 13.10.2026, Schritt 3 des Menueumbaus).
 *
 * Bis dahin wurde sie beim OEFFNEN DER SEITE gebaut - Karte, Mauern, Portale,
 * Rauch, Beleuchtung. Man sah nichts davon, weil das Menue darueber lag, aber
 * auf dem Handy kostete es Ladezeit und Akku, bevor man ueberhaupt wusste, ob
 * man spielen will.
 *
 * Jetzt entsteht sie erst, wenn man in einem Match ist - im Menue passiert
 * gar nichts.
 *
 * IM WARTERAUM LAEDT SIE SCHON, SICHTBAR IST SIE NICHT (Robin, 13.10.2026).
 *
 * Zuerst entstand sie erst zum 3-2-1. Das war zu spaet gedacht: Die paar
 * Sekunden Countdown muessen dann fuer das Laden reichen, und zwar auf dem
 * langsamsten der beiden Geraete - sonst steht einer vor einem leeren Bild,
 * waehrend der andere schon spielt. Im Warteraum wartet man ohnehin; die Zeit
 * ist geschenkt.
 *
 * Zwei Fragen, zwei Antworten:
 *
 *   brauchtSpielwelt - existiert sie ueberhaupt? (ab dem Beitritt)
 *   imWarteraum      - sitzt man gerade davor? (bis zum Kampf)
 *   weltSichtbar     - ist sie zu SEHEN? (beides zusammen)
 *
 * Dazu eine vierte Frage derselben Art: Gehoert die Meldung ueber die
 * Verbindung gerade ins Bild? Sie stand hier nicht, und prompt stand sie
 * blass hinter dem Hauptmenue.
 *
 * Eigene, winzige Regeln - weil "wann existiert die Welt" sonst als Bedingung
 * mitten in einem React-Effekt staende und niemand sie pruefen koennte.
 */

import type { ConnectionStatus } from '../net/MatchConnection.js';

/**
 * Soll die Spielwelt JETZT existieren?
 *
 * Vom Beitritt bis zum Verlassen - im Warteraum also schon, denn dort soll
 * sie fertig werden, bevor es losgeht. Im Ergebnisbildschirm bleibt sie
 * ebenfalls, denn der liegt ueber der Lane.
 *
 * EIN FUNKLOCH RAEUMT SIE NICHT AB. Bricht die Verbindung mitten im Match
 * weg, faellt der Zustand auf "getrennt" oder - beim Wiederverbinden - auf
 * "verbindet". Haenge man nur an "verbunden", wuerde die ganze Welt in dem
 * Augenblick weggeworfen und danach neu gebaut: ein paar Sekunden schwarz,
 * ausgerechnet dann, wenn es ohnehin klemmt.
 */
export function brauchtSpielwelt(stand: {
  status: ConnectionStatus;
  phase: string;
  /** Laeuft gerade ein Wiederverbinden? */
  reconnecting?: boolean;
}): boolean {
  if (stand.status === 'connected') return true;

  // Weggebrochen oder auf dem Rueckweg: Das Match laeuft weiter.
  return stand.reconnecting === true || stand.status === 'disconnected';
}

/**
 * Steht man gerade im Warteraum - also VOR dem eigentlichen Match?
 *
 * Dann ist die Welt zwar schon da, aber verborgen: Weder die Lane noch
 * irgendein Teil des HUD gehoert dorthin (Rueckmeldung vom 13.10.2026 -
 * "man sieht schon Faehigkeiten, Traenke, den Lebensbalken ... das soll
 * nicht sein"). Der Warteraum ist ein eigener Raum, kein halbes Match.
 *
 * Der 3-2-1 zaehlt dazu: Bis zum letzten Augenblick bleibt der Blick auf
 * der Zahl und nicht auf einer Oberflaeche, die noch nichts tut.
 */
export function imWarteraum(stand: {
  status: ConnectionStatus;
  phase: string;
  reconnecting?: boolean;
}): boolean {
  /*
   * Im Menue gibt es keinen Warteraum - dort liegt das Menue selbst darueber.
   *
   * Gefragt wird ueber brauchtSpielwelt und nicht ueber "verbunden": Bricht
   * die Verbindung im Warteraum weg, sitzt man immer noch dort. Sonst kaeme
   * ausgerechnet dann die Lane zum Vorschein.
   */
  if (!brauchtSpielwelt(stand)) return false;

  return stand.phase === 'waiting' || stand.phase === 'lobby' || stand.phase === 'starting';
}

/**
 * Ist die Spielwelt gerade zu SEHEN?
 *
 * Daran haengt nicht nur die Lane, sondern auch das ganze HUD: Anchorbalken,
 * Uhr, Faehigkeiten, Traenke, die Knoepfe. Sie gehoeren zur Welt und haben
 * ohne sie keinen Sinn.
 *
 * EINE Frage fuer beides, weil es zweimal schiefgegangen ist: Erst war das
 * HUD im Warteraum zu sehen, dann - blasser, aber doch - im Hauptmenue
 * (13.10.2026). Beide Male fehlte die Bedingung an genau einer Stelle. Wer
 * jetzt etwas zum HUD hinzufuegt, findet hier die eine Antwort.
 */
export function weltSichtbar(stand: { status: ConnectionStatus; phase: string }): boolean {
  return brauchtSpielwelt(stand) && !imWarteraum(stand);
}

/**
 * Gehoert die Meldung ueber die Verbindung gerade ins Bild?
 *
 * Nur dann, wenn eine Verbindung WEGGEBROCHEN ist - oder gerade wieder
 * aufgebaut wird. Dann sass man in einem Match und muss wissen, warum es
 * steht.
 *
 * Im Hauptmenue nicht (Rueckmeldung vom 13.10.2026: "Connection lost"
 * schimmerte dort durch). Dort gibt es keine Verbindung, die verloren gehen
 * koennte, und die beiden Faelle, die es dort wirklich gibt, sagt das Menue
 * selbst: "Connecting ..." und seine eigene, uebersetzte Fehlermeldung. Eine
 * zweite Anzeige daneben waere dieselbe Auskunft doppelt.
 */
export function zeigeVerbindungsmeldung(stand: {
  status: ConnectionStatus;
  reconnecting: boolean;
}): boolean {
  if (stand.reconnecting) return true;

  return stand.status === 'disconnected';
}
